import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Trophy, RefreshCw, Medal, Star, TrendingUp, ArrowRightLeft, Package, Battery, Wrench, ClipboardCheck, FileText } from 'lucide-react';

const BADGE_CONFIG = {
  EXPERT: { label: 'Expert', color: 'bg-amber-500/10 text-amber-600 border-amber-300', icon: Trophy },
  AVANSAT: { label: 'Avansat', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-300', icon: Star },
  ACTIV: { label: 'Activ', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-300', icon: TrendingUp },
  INCEPATOR: { label: 'Incepator', color: 'bg-slate-500/10 text-slate-500 border-slate-300', icon: Medal },
};

const RANK_STYLE = {
  1: 'bg-gradient-to-r from-amber-100 to-amber-50 border-amber-300',
  2: 'bg-gradient-to-r from-slate-100 to-slate-50 border-slate-300',
  3: 'bg-gradient-to-r from-orange-100 to-orange-50 border-orange-300',
};

export default function LeaderboardPage() {
  const [data, setData] = useState({ leaderboard: [], period_days: 30 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/leaderboard?days=${days}`);
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea leaderboard'); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="leaderboard-title">
            <Trophy className="w-7 h-7 text-amber-500" /> Clasament Eficienta
          </h1>
          <p className="text-sm text-slate-500 mt-1">Activitatea utilizatorilor in ultimele {days} zile</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="h-9 px-3 rounded-md border text-sm" data-testid="leaderboard-days">
          <option value={7}>7 zile</option>
          <option value={14}>14 zile</option>
          <option value={30}>30 zile</option>
          <option value={90}>90 zile</option>
        </select>
      </div>

      {/* Podium - Top 3 */}
      {data.leaderboard.length >= 3 && (
        <div className="grid grid-cols-3 gap-4">
          {[1, 0, 2].map(idx => {
            const u = data.leaderboard[idx];
            if (!u) return null;
            const cfg = BADGE_CONFIG[u.badge] || BADGE_CONFIG.INCEPATOR;
            const Icon = cfg.icon;
            const isFirst = idx === 0;
            return (
              <div key={u.name} className={`rounded-xl border-2 p-4 text-center ${isFirst ? 'md:-mt-4' : ''} ${RANK_STYLE[u.rank] || 'bg-white border-slate-200'}`} data-testid={`podium-${u.rank}`}>
                <div className={`text-3xl mb-2 ${u.rank === 1 ? 'text-4xl' : ''}`}>
                  {u.rank === 1 ? '🥇' : u.rank === 2 ? '🥈' : '🥉'}
                </div>
                <h3 className="font-bold text-slate-800 text-sm truncate">{u.name}</h3>
                <Badge className={`${cfg.color} border text-[10px] mt-1.5 gap-1`}><Icon className="w-3 h-3" /> {cfg.label}</Badge>
                <div className="text-2xl font-bold text-slate-700 mt-2">{u.score}</div>
                <div className="text-[10px] text-slate-400">puncte</div>
                <div className="text-xs text-slate-500 mt-2">{u.total_actions} actiuni</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaderboard-table">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="p-3 text-left text-xs font-semibold text-slate-500 w-12">#</th>
              <th className="p-3 text-left text-xs font-semibold text-slate-500">Utilizator</th>
              <th className="p-3 text-left text-xs font-semibold text-slate-500">Nivel</th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Tichete Inchise"><Wrench className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Mentenanta Preventiva"><ClipboardCheck className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Service Baterii"><Battery className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Receptii"><Package className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Cereri Materiale"><FileText className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500" title="Miscari Stoc"><ArrowRightLeft className="w-3.5 h-3.5 mx-auto" /></th>
              <th className="p-3 text-center text-xs font-semibold text-slate-500">Total</th>
              <th className="p-3 text-right text-xs font-semibold text-slate-500">Puncte</th>
            </tr>
          </thead>
          <tbody>
            {data.leaderboard.map(u => {
              const cfg = BADGE_CONFIG[u.badge] || BADGE_CONFIG.INCEPATOR;
              const Icon = cfg.icon;
              return (
                <tr key={u.user_id || u.name} className={`border-b ${u.rank <= 3 ? 'bg-amber-50/30' : ''}`} data-testid={`row-${u.rank}`}>
                  <td className="p-3 font-bold text-slate-400">
                    {u.rank <= 3 ? ['🥇', '🥈', '🥉'][u.rank - 1] : u.rank}
                  </td>
                  <td className="p-3 font-medium text-slate-800">{u.name}</td>
                  <td className="p-3">
                    <Badge className={`${cfg.color} border text-[10px] gap-1`}><Icon className="w-3 h-3" /> {cfg.label}</Badge>
                  </td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.tickets_closed || 0}</td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.preventive_done || 0}</td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.battery_services || 0}</td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.receipts || 0}</td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.material_requests || 0}</td>
                  <td className="p-3 text-center font-mono text-slate-600">{u.movements || 0}</td>
                  <td className="p-3 text-center font-mono font-bold text-slate-700">{u.total_actions}</td>
                  <td className="p-3 text-right font-bold text-indigo-600">{u.score}</td>
                </tr>
              );
            })}
            {data.leaderboard.length === 0 && (
              <tr><td colSpan={11} className="p-8 text-center text-slate-400">Nicio activitate inregistrata in aceasta perioada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Scoring Legend */}
      <div className="bg-slate-50 rounded-xl border p-4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Sistem Punctare</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <Wrench className="w-4 h-4 text-red-500" />
            <div>
              <p className="font-semibold text-slate-700">Tichete</p>
              <p className="text-slate-400">15 pct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <ClipboardCheck className="w-4 h-4 text-purple-500" />
            <div>
              <p className="font-semibold text-slate-700">Preventiva</p>
              <p className="text-slate-400">12 pct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <Battery className="w-4 h-4 text-cyan-500" />
            <div>
              <p className="font-semibold text-slate-700">Baterii</p>
              <p className="text-slate-400">10 pct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <Package className="w-4 h-4 text-emerald-500" />
            <div>
              <p className="font-semibold text-slate-700">Receptii</p>
              <p className="text-slate-400">8 pct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <FileText className="w-4 h-4 text-amber-500" />
            <div>
              <p className="font-semibold text-slate-700">Cereri</p>
              <p className="text-slate-400">5 pct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <ArrowRightLeft className="w-4 h-4 text-blue-500" />
            <div>
              <p className="font-semibold text-slate-700">Miscari</p>
              <p className="text-slate-400">2 pct</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
